import { useMemo, type HTMLAttributes } from "react";
import { Marked } from "marked";
import DOMPurify from "dompurify";

const markdown = new Marked({
  breaks: true,
  gfm: true,
});

const RICH_TEXT_PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "strong",
    "em",
    "b",
    "i",
    "u",
    "a",
    "ul",
    "ol",
    "li",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "pre",
    "code",
    "blockquote",
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
    "img",
    "hr",
    "span",
    "div",
    "del",
    "sup",
    "sub",
    "details",
    "summary",
  ],
  ALLOWED_ATTR: [
    "href",
    "target",
    "rel",
    "src",
    "alt",
    "class",
    "title",
    "width",
    "height",
    "open",
  ],
  ALLOW_DATA_ATTR: false,
};

let linkHookRegistered = false;

function ensureSanitizerHooks() {
  if (linkHookRegistered) return;

  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
  linkHookRegistered = true;
}

export function sanitizeRichHtml(html: string): string {
  ensureSanitizerHooks();
  return DOMPurify.sanitize(html, RICH_TEXT_PURIFY_CONFIG);
}

export function renderMarkdownHtml(content: string): string {
  const rawHtml = markdown.parse(content) as string;
  return sanitizeRichHtml(rawHtml);
}

type SanitizedHtmlElement = "article" | "div" | "section" | "span";

interface SanitizedHtmlProps extends HTMLAttributes<HTMLElement> {
  html: string;
  as?: SanitizedHtmlElement;
}

export function SanitizedHtml({
  html,
  as = "div",
  ...props
}: SanitizedHtmlProps) {
  const sanitizedHtml = useMemo(() => sanitizeRichHtml(html), [html]);
  const Element = as;

  return (
    <Element {...props} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
  );
}
